import React, { useEffect, useMemo, useRef } from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import cn from 'classnames';
import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';
import styled from 'styled-components';

import { SWrapper } from './styled';
import { withVideoErrorBoundary } from './HOC';

interface IProps extends ReactPlayerProps {
  className?: string;
}

const ELEVKANALEN_HOST = 'elevkanalen.no';

const VideoPlayer: React.FC<IProps> = (props) => {
  const reactPlayerRef = useRef<ReactPlayer>(null);
  const { className, ...playerProps } = props;
  const videoUrl = String(playerProps.url);
  const playerPropsWithDefault = {
    width: '100%',
    height: '100%',
    ...playerProps
  };

  // Derived during render because it picks the branch below: the previous
  // ref-written-in-effect version left the iframe branch permanently unmounted.
  const videoUrlHost = useMemo<string>(() => {
    try {
      return new URL(videoUrl).hostname;
    } catch (_e) {
      // A malformed url must not throw during render.
      return '';
    }
  }, [videoUrl]);

  const isElevkanalenUrl =
    videoUrlHost === ELEVKANALEN_HOST || videoUrlHost.endsWith(`.${ELEVKANALEN_HOST}`);

  useEffect(() => {
    try {
      /*
       * Unsolved bug on iOS devices (shortly, iOS prevents onReady method to get called)
       * https://github.com/CookPete/react-player/issues/340
       *
       * Solved with manual calling load() function in order to then execute onReady method
       * */
      reactPlayerRef?.current?.getInternalPlayer()?.load();
    } catch (_e) {
      /* noop */
    }
  }, [videoUrl]);

  const handleIframeLoad = (): void => {
    const { onReady } = playerProps;

    // No ReactPlayer instance exists on this branch (every consumer's onReady ignores its
    // argument), but without this call the embed never reports readiness.
    (onReady as ((player?: ReactPlayer) => void) | undefined)?.();
  };

  return (
    <SWrapper className={cn(className)}>
      {isElevkanalenUrl ? (
        <SIframe
          data-testid="elevkanalen-iframe"
          src={videoUrl}
          frameBorder="0"
          onLoad={handleIframeLoad}
        />
      ) : (
        <ReactPlayer
          {...playerPropsWithDefault}
          ref={reactPlayerRef}
          volume={playerProps.volume}
          onError={(...args: Parameters<NonNullable<ReactPlayerProps['onError']>>) => {
            captureMediaLoadError(args[0], {
              kind: 'video',
              src: videoUrl,
              context: 'embed'
            });
            // Forward every arg ReactPlayer emits (error, data, hls, hlsGlobal).
            playerProps.onError?.(...args);
          }}
        />
      )}
    </SWrapper>
  );
};

const SIframe = styled.iframe`
  width: 100%;
  height: 100%;
`;

export default withVideoErrorBoundary(VideoPlayer);
